package dairy.mynotes.utils

import kotlin.random.Random

/**
 * Pure-Kotlin AES-256-CTR + HMAC-SHA256 authenticated encryption.
 * Works identically on Android, iOS, and Web — no platform-specific code.
 */

const val ENC_PREFIX = "enc:v1:"
private const val APP_SALT = "MyNotes_AES256_Salt_v1"

// ── Public API ────────────────────────────────────────────────────────────────

fun encryptNote(plaintext: String, userId: String): String {
    return try {
        val key = deriveKey(userId)
        val iv = Random.nextBytes(12)
        val ciphertext = aesCtr(plaintext.encodeToByteArray(), key, iv)
        val mac = hmacSha256(key, iv + ciphertext)
        "$ENC_PREFIX${iv.toBase64()}:${(ciphertext + mac).toBase64()}"
    } catch (e: Throwable) {
        println("NoteEncryption: encryptNote failed — ${e.message}")
        plaintext
    }
}

fun decryptNote(encryptedText: String, userId: String): String {
    if (!encryptedText.startsWith(ENC_PREFIX)) return encryptedText
    if (userId.isEmpty()) return "[Unable to decrypt: no user]"
    return try {
        val key = deriveKey(userId)
        val parts = encryptedText.removePrefix(ENC_PREFIX).split(":")
        if (parts.size != 2) return "[Unable to decrypt: invalid format]"
        val iv = parts[0].fromBase64()
        val payload = parts[1].fromBase64()
        if (payload.size < 32) return "[Unable to decrypt: corrupted data]"
        val ciphertext = payload.copyOf(payload.size - 32)
        val storedMac = payload.copyOfRange(payload.size - 32, payload.size)
        val expectedMac = hmacSha256(key, iv + ciphertext)
        var diff = 0
        for (i in 0..31) diff = diff or (storedMac[i].toInt() xor expectedMac[i].toInt())
        if (diff != 0) {
            println("NoteEncryption: MAC mismatch for userId='$userId' ivLen=${iv.size} ctLen=${ciphertext.size}")
            return "[Note encrypted with old format — please delete and re-create]"
        }
        aesCtr(ciphertext, key, iv).decodeToString()
    } catch (e: Throwable) {
        println("NoteEncryption: decryptNote failed — ${e.message}")
        "[Unable to decrypt: ${e.message}]"
    }
}

// ── Key derivation ────────────────────────────────────────────────────────────

private fun deriveKey(userId: String): ByteArray = sha256Bytes((userId + APP_SALT).encodeToByteArray())

// ── AES-256 key schedule ──────────────────────────────────────────────────────

private fun aesKeyExpansion(key: ByteArray): IntArray {
    require(key.size == 32)
    val w = IntArray(60)
    for (i in 0..7) {
        w[i] = ((key[i*4    ].toInt() and 0xFF) shl 24) or
               ((key[i*4 + 1].toInt() and 0xFF) shl 16) or
               ((key[i*4 + 2].toInt() and 0xFF) shl  8) or
                (key[i*4 + 3].toInt() and 0xFF)
    }
    for (i in 8..59) {
        var temp = w[i-1]
        temp = when {
            i % 8 == 0 -> subWord(rotWord(temp)) xor RCON[i/8 - 1]
            i % 8 == 4 -> subWord(temp)
            else -> temp
        }
        w[i] = w[i-8] xor temp
    }
    return w
}

private fun rotWord(w: Int) = (w shl 8) or (w ushr 24)

private fun subWord(w: Int): Int =
    (SBOX[(w ushr 24) and 0xFF] shl 24) or
    (SBOX[(w ushr 16) and 0xFF] shl 16) or
    (SBOX[(w ushr  8) and 0xFF] shl  8) or
     SBOX[ w          and 0xFF]

// ── AES block encrypt ─────────────────────────────────────────────────────────

private fun aesBlock(block: ByteArray, rk: IntArray): ByteArray {
    // State: s[row][col], stored column-major in input (standard AES)
    val s = Array(4) { row -> IntArray(4) { col -> block[row + col * 4].toInt() and 0xFF } }

    fun addRoundKey(round: Int) {
        for (col in 0..3) {
            val ww = rk[round * 4 + col]
            s[0][col] = s[0][col] xor ((ww ushr 24) and 0xFF)
            s[1][col] = s[1][col] xor ((ww ushr 16) and 0xFF)
            s[2][col] = s[2][col] xor ((ww ushr  8) and 0xFF)
            s[3][col] = s[3][col] xor  (ww           and 0xFF)
        }
    }

    fun subBytes() {
        for (r in 0..3) for (c in 0..3) s[r][c] = SBOX[s[r][c]]
    }

    fun shiftRows() {
        val t1 = s[1][0]; s[1][0] = s[1][1]; s[1][1] = s[1][2]; s[1][2] = s[1][3]; s[1][3] = t1
        var t = s[2][0]; s[2][0] = s[2][2]; s[2][2] = t; t = s[2][1]; s[2][1] = s[2][3]; s[2][3] = t
        val t3 = s[3][3]; s[3][3] = s[3][2]; s[3][2] = s[3][1]; s[3][1] = s[3][0]; s[3][0] = t3
    }

    fun xt(b: Int) = if (b and 0x80 != 0) ((b shl 1) xor 0x1B) and 0xFF else (b shl 1) and 0xFF

    fun mixColumns() {
        for (c in 0..3) {
            val a0 = s[0][c]; val a1 = s[1][c]; val a2 = s[2][c]; val a3 = s[3][c]
            s[0][c] = xt(a0) xor xt(a1) xor a1 xor a2 xor a3
            s[1][c] = a0 xor xt(a1) xor xt(a2) xor a2 xor a3
            s[2][c] = a0 xor a1 xor xt(a2) xor xt(a3) xor a3
            s[3][c] = xt(a0) xor a0 xor a1 xor a2 xor xt(a3)
        }
    }

    addRoundKey(0)
    for (round in 1..13) { subBytes(); shiftRows(); mixColumns(); addRoundKey(round) }
    subBytes(); shiftRows(); addRoundKey(14)

    val out = ByteArray(16)
    for (row in 0..3) for (col in 0..3) out[row + col * 4] = s[row][col].toByte()
    return out
}

// ── AES-CTR mode ──────────────────────────────────────────────────────────────

private fun aesCtr(data: ByteArray, key: ByteArray, iv: ByteArray): ByteArray {
    val rk = aesKeyExpansion(key)
    val out = ByteArray(data.size)
    val ctr = ByteArray(16)
    iv.copyInto(ctr, 0, 0, minOf(iv.size, 12))
    ctr[15] = 1  // counter starts at 1
    var pos = 0; var blkNum = 1
    while (pos < data.size) {
        val ks = aesBlock(ctr, rk)
        val chunk = minOf(16, data.size - pos)
        for (i in 0 until chunk) out[pos + i] = (data[pos + i].toInt() xor ks[i].toInt()).toByte()
        pos += chunk; blkNum++
        ctr[15] = (blkNum and 0xFF).toByte()
        ctr[14] = ((blkNum ushr  8) and 0xFF).toByte()
        ctr[13] = ((blkNum ushr 16) and 0xFF).toByte()
        ctr[12] = ((blkNum ushr 24) and 0xFF).toByte()
    }
    return out
}

// ── SHA-256 ───────────────────────────────────────────────────────────────────

@Suppress("INTEGER_OVERFLOW")
fun sha256Bytes(msg: ByteArray): ByteArray {
    var h0 = 0x6a09e667;      var h1 = 0xbb67ae85.toInt()
    var h2 = 0x3c6ef372;      var h3 = 0xa54ff53a.toInt()
    var h4 = 0x510e527f;      var h5 = 0x9b05688c.toInt()
    var h6 = 0x1f83d9ab;      var h7 = 0x5be0cd19

    val bitLen = msg.size.toLong() * 8
    val origLen = msg.size
    // Padding to next multiple of 64, with room for 0x80 and 8-byte length
    val padded = ByteArray(((origLen + 9 + 63) / 64) * 64)
    msg.copyInto(padded)
    padded[origLen] = 0x80.toByte()
    for (i in 0..7) padded[padded.size - 8 + i] = ((bitLen ushr (56 - i * 8)) and 0xFF).toByte()

    val w = IntArray(64)
    var cs = 0
    while (cs < padded.size) {
        // Load words — parentheses are critical to avoid operator precedence bugs
        for (i in 0..15) {
            w[i] = ((padded[cs + i*4    ].toInt() and 0xFF) shl 24) or
                   ((padded[cs + i*4 + 1].toInt() and 0xFF) shl 16) or
                   ((padded[cs + i*4 + 2].toInt() and 0xFF) shl  8) or
                    (padded[cs + i*4 + 3].toInt() and 0xFF)
        }
        for (i in 16..63) {
            val s0 = w[i-15].rotR(7) xor w[i-15].rotR(18) xor (w[i-15] ushr 3)
            val s1 = w[i-2 ].rotR(17) xor w[i-2 ].rotR(19) xor (w[i-2 ] ushr 10)
            w[i] = w[i-16] + s0 + w[i-7] + s1
        }
        var a=h0; var b=h1; var c=h2; var d=h3; var e=h4; var f=h5; var g=h6; var h=h7
        for (i in 0..63) {
            val t1 = h + e.rotR(6).xor(e.rotR(11)).xor(e.rotR(25)) + ((e and f) xor (e.inv() and g)) + SHA256_K[i] + w[i]
            val t2 = a.rotR(2).xor(a.rotR(13)).xor(a.rotR(22)) + ((a and b) xor (a and c) xor (b and c))
            h=g; g=f; f=e; e=d+t1; d=c; c=b; b=a; a=t1+t2
        }
        h0+=a; h1+=b; h2+=c; h3+=d; h4+=e; h5+=f; h6+=g; h7+=h
        cs += 64
    }

    fun i2b(v: Int, off: Int, out: ByteArray) {
        out[off]=(v ushr 24).toByte(); out[off+1]=(v ushr 16).toByte()
        out[off+2]=(v ushr 8).toByte(); out[off+3]=v.toByte()
    }
    val digest = ByteArray(32)
    i2b(h0,0,digest); i2b(h1,4,digest); i2b(h2,8,digest);  i2b(h3,12,digest)
    i2b(h4,16,digest); i2b(h5,20,digest); i2b(h6,24,digest); i2b(h7,28,digest)
    return digest
}

private fun Int.rotR(n: Int) = (this ushr n) or (this shl (32 - n))

// ── HMAC-SHA256 ───────────────────────────────────────────────────────────────

private fun hmacSha256(key: ByteArray, data: ByteArray): ByteArray {
    val k = if (key.size > 64) sha256Bytes(key) else key
    val kp = ByteArray(64); k.copyInto(kp)
    val ipad = ByteArray(64) { (kp[it].toInt() xor 0x36).toByte() }
    val opad = ByteArray(64) { (kp[it].toInt() xor 0x5C).toByte() }
    return sha256Bytes(opad + sha256Bytes(ipad + data))
}

// ── Base64 ────────────────────────────────────────────────────────────────────

private val B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/"

private fun ByteArray.toBase64(): String {
    val sb = StringBuilder()
    var i = 0
    while (i < size) {
        val b0 = this[i].toInt() and 0xFF
        val b1 = if (i+1 < size) this[i+1].toInt() and 0xFF else 0
        val b2 = if (i+2 < size) this[i+2].toInt() and 0xFF else 0
        sb.append(B64[b0 ushr 2])
        sb.append(B64[((b0 and 3) shl 4) or (b1 ushr 4)])
        sb.append(if (i+1 < size) B64[((b1 and 0xF) shl 2) or (b2 ushr 6)] else '=')
        sb.append(if (i+2 < size) B64[b2 and 0x3F] else '=')
        i += 3
    }
    return sb.toString()
}

private fun String.fromBase64(): ByteArray {
    val s = trimEnd('=')
    val out = ByteArray(s.length * 3 / 4 + 3)
    var oi = 0; var i = 0
    while (i < s.length) {
        val c0 = B64.indexOf(s[i]); i++
        val c1 = if (i < s.length) B64.indexOf(s[i]) else -1; i++
        val c2 = if (i < s.length) B64.indexOf(s[i]) else -1; i++
        val c3 = if (i < s.length) B64.indexOf(s[i]) else -1; i++
        if (c0 >= 0 && c1 >= 0) out[oi++] = ((c0 shl 2) or (c1 ushr 4)).toByte()
        if (c1 >= 0 && c2 >= 0) out[oi++] = ((c1 shl 4) or (c2 ushr 2)).toByte()
        if (c2 >= 0 && c3 >= 0) out[oi++] = ((c2 shl 6) or  c3         ).toByte()
    }
    return out.copyOf(oi)
}

// ── AES constants ─────────────────────────────────────────────────────────────

private val SBOX = intArrayOf(
    0x63,0x7c,0x77,0x7b,0xf2,0x6b,0x6f,0xc5,0x30,0x01,0x67,0x2b,0xfe,0xd7,0xab,0x76,
    0xca,0x82,0xc9,0x7d,0xfa,0x59,0x47,0xf0,0xad,0xd4,0xa2,0xaf,0x9c,0xa4,0x72,0xc0,
    0xb7,0xfd,0x93,0x26,0x36,0x3f,0xf7,0xcc,0x34,0xa5,0xe5,0xf1,0x71,0xd8,0x31,0x15,
    0x04,0xc7,0x23,0xc3,0x18,0x96,0x05,0x9a,0x07,0x12,0x80,0xe2,0xeb,0x27,0xb2,0x75,
    0x09,0x83,0x2c,0x1a,0x1b,0x6e,0x5a,0xa0,0x52,0x3b,0xd6,0xb3,0x29,0xe3,0x2f,0x84,
    0x53,0xd1,0x00,0xed,0x20,0xfc,0xb1,0x5b,0x6a,0xcb,0xbe,0x39,0x4a,0x4c,0x58,0xcf,
    0xd0,0xef,0xaa,0xfb,0x43,0x4d,0x33,0x85,0x45,0xf9,0x02,0x7f,0x50,0x3c,0x9f,0xa8,
    0x51,0xa3,0x40,0x8f,0x92,0x9d,0x38,0xf5,0xbc,0xb6,0xda,0x21,0x10,0xff,0xf3,0xd2,
    0xcd,0x0c,0x13,0xec,0x5f,0x97,0x44,0x17,0xc4,0xa7,0x7e,0x3d,0x64,0x5d,0x19,0x73,
    0x60,0x81,0x4f,0xdc,0x22,0x2a,0x90,0x88,0x46,0xee,0xb8,0x14,0xde,0x5e,0x0b,0xdb,
    0xe0,0x32,0x3a,0x0a,0x49,0x06,0x24,0x5c,0xc2,0xd3,0xac,0x62,0x91,0x95,0xe4,0x79,
    0xe7,0xc8,0x37,0x6d,0x8d,0xd5,0x4e,0xa9,0x6c,0x56,0xf4,0xea,0x65,0x7a,0xae,0x08,
    0xba,0x78,0x25,0x2e,0x1c,0xa6,0xb4,0xc6,0xe8,0xdd,0x74,0x1f,0x4b,0xbd,0x8b,0x8a,
    0x70,0x3e,0xb5,0x66,0x48,0x03,0xf6,0x0e,0x61,0x35,0x57,0xb9,0x86,0xc1,0x1d,0x9e,
    0xe1,0xf8,0x98,0x11,0x69,0xd9,0x8e,0x94,0x9b,0x1e,0x87,0xe9,0xce,0x55,0x28,0xdf,
    0x8c,0xa1,0x89,0x0d,0xbf,0xe6,0x42,0x68,0x41,0x99,0x2d,0x0f,0xb0,0x54,0xbb,0x16
)

private val RCON = intArrayOf(
    0x01000000, 0x02000000, 0x04000000, 0x08000000, 0x10000000,
    0x20000000, 0x40000000, 0x80000000.toInt(), 0x1b000000, 0x36000000,
    0x6c000000, 0xd8000000.toInt(), 0xab000000.toInt(), 0x4d000000, 0x9a000000.toInt()
)

private val SHA256_K = intArrayOf(
    0x428a2f98.toInt(), 0x71374491, 0xb5c0fbcf.toInt(), 0xe9b5dba5.toInt(),
    0x3956c25b, 0x59f111f1, 0x923f82a4.toInt(), 0xab1c5ed5.toInt(),
    0xd807aa98.toInt(), 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe.toInt(), 0x9bdc06a7.toInt(), 0xc19bf174.toInt(),
    0xe49b69c1.toInt(), 0xefbe4786.toInt(), 0x0fc19dc6, 0x240ca1cc,
    0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152.toInt(), 0xa831c66d.toInt(), 0xb00327c8.toInt(), 0xbf597fc7.toInt(),
    0xc6e00bf3.toInt(), 0xd5a79147.toInt(), 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e.toInt(), 0x92722c85.toInt(),
    0xa2bfe8a1.toInt(), 0xa81a664b.toInt(), 0xc24b8b70.toInt(), 0xc76c51a3.toInt(),
    0xd192e819.toInt(), 0xd6990624.toInt(), 0xf40e3585.toInt(), 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
    0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814.toInt(), 0x8cc70208.toInt(),
    0x90befffa.toInt(), 0xa4506ceb.toInt(), 0xbef9a3f7.toInt(), 0xc67178f2.toInt()
)

